import React, { useState } from 'react';
import { Calendar, Check, X, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';

const VacationsPanel = ({ 
  user,
  vacationRequests,
  pendingRequests,
  approvedTeamRequests,
  loading,
  onRequestVacation,
  onApproveRequest,
  onRejectRequest,
  onCancelRequest
}) => {
  const [vacationForm, setVacationForm] = useState({
    start_date: '',
    end_date: '',
    reason: ''
  });
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(null);

  const isManager = user?.role === 'manager' || user?.role === 'admin';

  const handleVacationRequest = async (e) => {
    e.preventDefault();
    const success = await onRequestVacation(vacationForm);
    if (success) {
      setVacationForm({ start_date: '', end_date: '', reason: '' });
      setShowRequestForm(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', { 
      day: '2-digit', month: '2-digit', year: 'numeric' 
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-700">Aprobada</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700">Rechazada</Badge>;
      case 'cancelled':
        return <Badge className="bg-slate-100 text-slate-700">Anulada</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-700">Pendiente</Badge>;
    }
  };

  const canCancel = (request) => {
    // Can't cancel if already cancelled or rejected
    if (request.status === 'cancelled' || request.status === 'rejected') return false;
    // Employee can cancel own pending or approved requests
    if (request.user_id === user?.id) return true;
    // Manager can cancel approved requests of their employees
    if (isManager && request.status === 'approved') return true;
    return false;
  };

  const handleCancelConfirm = async () => {
    if (cancelConfirm && onCancelRequest) {
      await onCancelRequest(cancelConfirm.id);
      setCancelConfirm(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Vacation Balance Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Mis Vacaciones
            </span>
            <Button onClick={() => setShowRequestForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nueva Solicitud
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <p className="text-sm text-blue-600">Días Totales</p>
              <p className="text-3xl font-bold text-blue-700">{user?.vacation_days || 0}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <p className="text-sm text-green-600">Días Disfrutados</p>
              <p className="text-3xl font-bold text-green-700">{user?.used_vacation_days || 0}</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-4 text-center">
              <p className="text-sm text-orange-600">Días Disponibles</p>
              <p className="text-3xl font-bold text-orange-700">
                {(user?.vacation_days || 0) - (user?.used_vacation_days || 0)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* My Requests */}
      <Card>
        <CardHeader>
          <CardTitle>Mis Solicitudes</CardTitle>
        </CardHeader>
        <CardContent>
          {vacationRequests && vacationRequests.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha Inicio</TableHead>
                  <TableHead>Fecha Fin</TableHead>
                  <TableHead>Días</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Comentarios</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vacationRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>{formatDate(request.start_date)}</TableCell>
                    <TableCell>{formatDate(request.end_date)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{request.days_requested}</Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">{request.reason}</TableCell>
                    <TableCell>{getStatusBadge(request.status)}</TableCell>
                    <TableCell className="max-w-[150px] truncate">
                      {request.manager_comments || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {canCancel(request) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setCancelConfirm(request)}
                          className="text-red-600 hover:text-red-700"
                          disabled={loading}
                          title="Anular solicitud"
                          data-testid={`cancel-my-vacation-${request.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center text-slate-500 py-4">No tienes solicitudes de vacaciones</p>
          )}
        </CardContent>
      </Card>

      {/* Pending Approvals (Manager only) */}
      {isManager && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {pendingRequests && pendingRequests.length > 0 && (
                <Badge variant="destructive">{pendingRequests.length}</Badge>
              )}
              Solicitudes Pendientes de Aprobación
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pendingRequests && pendingRequests.length > 0 ? (
              <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Empleado</TableHead>
                  <TableHead>Fecha Inicio</TableHead>
                  <TableHead>Fecha Fin</TableHead>
                  <TableHead>Días</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{request.user_name}</p>
                        <p className="text-sm text-slate-500">{request.user_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(request.start_date)}</TableCell>
                    <TableCell>{formatDate(request.end_date)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{request.days_requested}</Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">{request.reason}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => onApproveRequest(request.id, 'approved')}
                          disabled={loading}
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Aprobar
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => onRejectRequest(request.id, 'rejected')}
                          disabled={loading}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Rechazar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            ) : (
              <p className="text-center text-slate-500 py-4">No hay solicitudes pendientes de aprobación</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Approved Team Vacations - Manager can cancel (Manager only) */}
      {isManager && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-600" />
              Vacaciones Aprobadas del Equipo
              {approvedTeamRequests && approvedTeamRequests.length > 0 && (
                <Badge className="bg-green-100 text-green-700">{approvedTeamRequests.length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {approvedTeamRequests && approvedTeamRequests.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empleado</TableHead>
                    <TableHead>Fecha Inicio</TableHead>
                    <TableHead>Fecha Fin</TableHead>
                    <TableHead>Días</TableHead>
                    <TableHead>Motivo</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {approvedTeamRequests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{request.user_name}</p>
                          <p className="text-sm text-slate-500">{request.user_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(request.start_date)}</TableCell>
                      <TableCell>{formatDate(request.end_date)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{request.days_requested}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">{request.reason}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setCancelConfirm(request)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          disabled={loading}
                          title="Anular vacaciones"
                          data-testid={`cancel-team-vacation-${request.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Anular
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-center text-slate-500 py-4">No hay vacaciones aprobadas de tu equipo</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Request Vacation Dialog */}
      <Dialog open={showRequestForm} onOpenChange={setShowRequestForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Solicitar Vacaciones</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleVacationRequest} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_date">Fecha Inicio</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={vacationForm.start_date}
                  onChange={(e) => setVacationForm({...vacationForm, start_date: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="end_date">Fecha Fin</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={vacationForm.end_date}
                  onChange={(e) => setVacationForm({...vacationForm, end_date: e.target.value})}
                  min={vacationForm.start_date}
                  required
                />
              </div>
            </div>
            <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
              💡 Puedes solicitar vacaciones retroactivas (fechas pasadas) si es necesario
            </p>
            <div>
              <Label htmlFor="reason">Motivo</Label>
              <Textarea
                id="reason"
                value={vacationForm.reason}
                onChange={(e) => setVacationForm({...vacationForm, reason: e.target.value})}
                placeholder="Describe el motivo de tu solicitud..."
                required
              />
            </div>
            <p className="text-sm text-slate-500">
              Días disponibles: {(user?.vacation_days || 0) - (user?.used_vacation_days || 0)}
            </p>
            <div className="flex gap-2">
              <Button type="submit" disabled={loading} className="flex-1">
                {loading ? 'Enviando...' : 'Enviar Solicitud'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowRequestForm(false)}>
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={!!cancelConfirm} onOpenChange={() => setCancelConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirmar Anulación de Vacaciones
            </DialogTitle>
            <DialogDescription className="text-slate-600 pt-2">
              {cancelConfirm && (
                <>
                  {cancelConfirm.user_id === user?.id ? (
                    <span>¿Estás seguro de que deseas anular tu solicitud de vacaciones?</span>
                  ) : (
                    <span>¿Estás seguro de que deseas anular las vacaciones de <strong>{cancelConfirm.user_name}</strong>?</span>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {cancelConfirm && (
            <div className="bg-slate-50 rounded-lg p-4 my-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-slate-500">Fecha Inicio:</span>
                  <p className="font-medium">{formatDate(cancelConfirm.start_date)}</p>
                </div>
                <div>
                  <span className="text-slate-500">Fecha Fin:</span>
                  <p className="font-medium">{formatDate(cancelConfirm.end_date)}</p>
                </div>
                <div>
                  <span className="text-slate-500">Días:</span>
                  <p className="font-medium">{cancelConfirm.days_requested}</p>
                </div>
                <div>
                  <span className="text-slate-500">Estado:</span>
                  <p className="font-medium">{cancelConfirm.status === 'approved' ? 'Aprobada' : 'Pendiente'}</p>
                </div>
              </div>
              {cancelConfirm.status === 'approved' && (
                <p className="text-green-600 text-sm mt-3 font-medium">
                  ✓ Los días serán restaurados al saldo del empleado
                </p>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setCancelConfirm(null)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button 
              variant="destructive"
              onClick={handleCancelConfirm}
              disabled={loading}
              data-testid="confirm-cancel-vacation"
            >
              {loading ? 'Anulando...' : 'Sí, Anular Vacaciones'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VacationsPanel;
